import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import StepSelectToolType from './billToFlowSteps/StepSelectToolType';
import StepConfigureTool from './billToFlowSteps/StepConfigureTool';
import StepSetParameters from './billToFlowSteps/StepSetParameters';
import StepReviewConfirm from './billToFlowSteps/StepReviewConfirm';
import StepToolCreated from './billToFlowSteps/StepToolCreated';
import { useAuth } from '../../context/AuthContext';
import { saveMyTool } from '../../utils/billToFlowTools';

const STEPS = [
  { label: 'Select Tool Type' },
  { label: 'Configure Tool' },
  { label: 'Set Parameters' },
  { label: 'Review & Confirm' },
  { label: 'Tool Created' },
];

const INITIAL_DATA = {
  toolType: '',
  categories: [],
  itemsSelection: 'all',
  toolName: '',
  toolCode: '',
  toolDescription: '',
  toolDate: '',
  parameters: {
    leadTimeDays: '',
    bufferPercent: '',
    projectedGrowth: '',
    salesHistoryWeeks: '',
    includeClosingStock: true,
    includeIssuedStock: true,
    includeProductionStock: false,
    includeDamagedStock: false,
    includeReturnsStock: false,
  },
};

function canProceed(step, data) {
  if (step === 0) return Boolean(data.toolType);
  if (step === 1)
    return data.categories.length > 0 && String(data.toolName || '').trim() !== '';
  if (step === 2) {
    const p = data.parameters || {};
    return (
      String(p.leadTimeDays ?? '').trim() !== '' &&
      String(p.bufferPercent ?? '').trim() !== '' &&
      String(p.projectedGrowth ?? '').trim() !== '' &&
      String(p.salesHistoryWeeks ?? '').trim() !== ''
    );
  }
  return true;
}

function Stepper({ currentStep }) {
  return (
    <div className="flex items-center justify-between gap-1">
      {STEPS.map((step, idx) => {
        const isCompleted = idx < currentStep;
        const isCurrent = idx === currentStep;
        const isUpcoming = idx > currentStep;

        return (
          <React.Fragment key={step.label}>
            {idx > 0 && (
              <div
                className={`hidden h-0.5 flex-1 sm:block ${
                  isCompleted ? 'bg-brand' : 'bg-gray-200'
                }`}
              />
            )}
            <div className="flex flex-col items-center gap-1">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                  isCompleted
                    ? 'bg-brand text-white'
                    : isCurrent
                      ? 'border-2 border-brand bg-white text-brand'
                      : 'border-2 border-gray-200 bg-white text-gray-400'
                }`}
              >
                {isCompleted ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  idx + 1
                )}
              </div>
              <span
                className={`hidden text-center text-sm leading-tight sm:block ${
                  isCurrent
                    ? 'font-semibold text-brand'
                    : isCompleted
                      ? 'font-medium text-brand'
                      : 'text-gray-400'
                } ${isUpcoming ? '' : ''}`}
              >
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function BillToFlowWizard() {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState(INITIAL_DATA);

  const updateWizardData = useCallback((partial) => {
    setWizardData((prev) => ({ ...prev, ...partial }));
  }, []);

  const goNext = () => {
    if (currentStep < STEPS.length - 1 && canProceed(currentStep, wizardData)) {
      setCurrentStep((s) => s + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const handleCreateTool = () => {
    saveMyTool({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      toolType: wizardData.toolType,
      toolName: wizardData.toolName,
      toolCode: wizardData.toolCode,
      toolDate: wizardData.toolDate,
      categories: wizardData.categories,
      parameters: wizardData.parameters,
      createdAt: new Date().toISOString(),
      createdBy: user?.name || user?.employee_name || user?.account_name || '—',
    });
    setCurrentStep(4);
  };

  const handleReset = () => {
    setWizardData(INITIAL_DATA);
    setCurrentStep(0);
  };

  const isLastFormStep = currentStep === 3;
  const isSuccessStep = currentStep === 4;

  return (
    <DashboardLayout title="Create Projected Tool">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            <Link to="/dashboard/admin" className="text-brand hover:underline">
              Home
            </Link>
            {' / '}
            <Link to="/dashboard/admin/bill-to-flow" className="text-brand hover:underline">
              My Tools
            </Link>
            {' / '}
            <span className="font-medium text-gray-700">Create Projected Tool</span>
          </p>
          <Link
            to="/dashboard/admin/bill-to-flow"
            className="text-sm font-medium text-brand hover:underline"
          >
            ← Back to My Tools
          </Link>
        </div>

        {/* Stepper */}
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:px-8 sm:py-5">
          <Stepper currentStep={currentStep} />
        </div>

        {/* Step Content */}
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          {currentStep === 0 && (
            <StepSelectToolType wizardData={wizardData} onChange={updateWizardData} />
          )}
          {currentStep === 1 && (
            <StepConfigureTool wizardData={wizardData} onChange={updateWizardData} />
          )}
          {currentStep === 2 && (
            <StepSetParameters wizardData={wizardData} onChange={updateWizardData} />
          )}
          {currentStep === 3 && <StepReviewConfirm wizardData={wizardData} />}
          {currentStep === 4 && (
            <StepToolCreated wizardData={wizardData} onReset={handleReset} />
          )}
        </div>

        {/* Navigation Buttons */}
        {!isSuccessStep && (
          <div className="flex items-center justify-between pb-6">
            <div>
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={goBack}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  Back
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={isLastFormStep ? handleCreateTool : goNext}
              disabled={!canProceed(currentStep, wizardData)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLastFormStep ? 'Create Tool' : 'Next'}
              {!isLastFormStep && (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default BillToFlowWizard;
